import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { useStudioAgentName, useThemeColors, useThemeFonts, useWidgetConfig } from "@brandclub/bc-widgets";
import { useAppConfig, useBrandAppConfig } from "../api/cache/appConfigQueryOptions.js";
import { getBrandLogo } from "../utils/assetUtils.js";
import { useRef, useMemo, useState, useEffect, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Toaster, toast } from "sonner";
import { setConversationSharing } from "./api.js";
import { getHeadersWithUniqueVisitorId } from "../Auth/index.js";
import { ConciergeChatAreaV2 } from "./components/ConciergeChatAreaV2/index.js";
import { ConciergeHeader } from "./components/ConciergeHeader/index.js";
import { ConciergeSidebar } from "./components/ConciergeSidebar.js";
import { SupportThemeProvider } from "./components/SupportThemeProvider.js";
import { ShareConversationDialog } from "./components/ShareConversationDialog.js";
import { useAutoScroll } from "./hooks/useAutoScroll.js";
import { useAutoSendFromUrl } from "./hooks/useAutoSendFromUrl.js";
import { useConversationLoader } from "./hooks/useConversationLoader.js";
import { useConciergeContext } from "./ConciergeContextProvider.js";
import DevOptionsPanel from "./components/DevOptionsPanel.js";
import { useDevOptions } from "./hooks/useDevOptions.js";
import { useConciergeChat } from "./hooks/useConciergeChat.js";
import { useConciergeAgreementGate } from "./hooks/useConciergeAgreementGate.js";
import { ConciergeAgreementCard } from "./components/ConciergeAgreementCard.js";
import { track } from "../analytics/AnalyticsService.js";
import { AnalyticsTrackingEvent } from "../analytics/events.js";
import { useConciergeTracking } from "./hooks/useConciergeTracking.js";
import { usePageContext } from "./hooks/usePageContext.js";
import { useUrlSync } from "./hooks/useUrlSync.js";
import { useConversationRefresh } from "./hooks/useConversations.js";
import { Drawer } from "@mui/material";
import { BRANDCLUB_BRAND_ID, BRANDCLUB_BRAND_NAME } from "../utils/constants/Constants.js";
const ConciergeContainer = ({
  layout = "header",
  isEmbedded,
  cartCount = 0,
  onBagClick,
  isCheckoutDrawerOpen = false,
  CheckoutDrawerContent,
  onVariantSelect,
  userProfile,
  onSignOutClick,
  onProfileClick,
  onPurchasesClick,
  onHomeClick,
  userFirstName,
  onChatsClick
}) => {
  var _a;
  const conversationState = useConciergeContext();
  const initialConversationIdRef = useRef(conversationState.conversationId);
  const initialSharedConversationIdRef = useRef(conversationState.sharedConversationId);
  const params = new URLSearchParams(window.location.search);
  const appConfigId = params.get("appConfigId") || null;
  const viewToken = useMemo(() => new URLSearchParams(window.location.search).get("viewToken"), []);
  const viewMode = useMemo(() => viewToken ? "read-only" : void 0, [viewToken]);
  const { data: appConfig } = useAppConfig();
  const { data: brandAppConfig } = useBrandAppConfig();
  const studioAgentName = useStudioAgentName();
  const { themeColors } = useThemeColors();
  const { themeFonts } = useThemeFonts();
  const sidebarFontConfig = useWidgetConfig("concierge-sidebar", {});
  const brandId = useMemo(
    () => {
      var _a2;
      return appConfig ? ((_a2 = appConfig.domainConfig) == null ? void 0 : _a2.brandId) || BRANDCLUB_BRAND_ID : void 0;
    },
    [appConfig]
  );
  const appLogoUrl = useMemo(() => brandId ? getBrandLogo(brandId) : void 0, [brandId]);
  const appName = useMemo(
    () => {
      var _a2;
      return appConfig ? ((_a2 = appConfig.domainConfig) == null ? void 0 : _a2.brandName) || BRANDCLUB_BRAND_NAME : void 0;
    },
    [appConfig]
  );
  const brandFontFamily = useMemo(() => {
    const fontKey = sidebarFontConfig.fontFamily;
    const fontData = fontKey && fontKey !== "inherit" ? themeFonts == null ? void 0 : themeFonts[fontKey] : null;
    if (fontData) {
      return fontData.format === "system" ? fontData.url : `"${fontData.name}", sans-serif`;
    }
    const bodyFont = themeFonts == null ? void 0 : themeFonts.body;
    if (bodyFont) {
      return bodyFont.format === "system" ? bodyFont.url : `"${bodyFont.name}", sans-serif`;
    }
    return void 0;
  }, [themeFonts, sidebarFontConfig.fontFamily]);
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState("");
  const [liveTakeoverActiveConversationId, setLiveTakeoverActiveConversationId] = useState(null);
  const devOpts = useDevOptions();
  const [streamErrorMessage, setStreamErrorMessage] = useState(null);
  const handleStreamError = (error, originalMessage) => {
    var _a2;
    setSearchInputValue(originalMessage);
    const msg = ((_a2 = error == null ? void 0 : error.message) == null ? void 0 : _a2.toLowerCase()) ?? "";
    let errorType = "unknown";
    if (msg.includes("429") || msg.includes("rate limit")) errorType = "rate_limit";
    else if (msg.includes("400") || msg.includes("bad request")) errorType = "bad_request";
    else if (msg.includes("network") || msg.includes("failed to fetch")) errorType = "network";
    else if (msg.includes("500") || msg.includes("server")) errorType = "server";
    const errorMessages = {
      rate_limit: "Too many requests. Please wait a moment before trying again.",
      bad_request: "Please enter a message before sending.",
      network: "Network error. Please check your connection and try again.",
      server: "Something went wrong on our end. Please try again.",
      unknown: "Something went wrong. Please try again."
    };
    setStreamErrorMessage(errorMessages[errorType]);
  };
  const [isLiveConversation, setIsLiveConversation] = useState(false);
  const streamAssignedIdRef = useRef(false);
  const freshMemory = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("freshMemory") === "true";
  const chat = useConciergeChat({
    apiBaseUrl: conversationState.apiBaseUrl,
    conversationId: conversationState.conversationId,
    sharedConversationId: conversationState.sharedConversationId,
    brandId: (brandId == null ? void 0 : brandId.toString()) ?? null,
    appConfigId,
    devOptions: devOpts.devOptions,
    freshMemory,
    onError: (error) => handleStreamError(error, searchInputValue),
    onTripwire: (message) => {
      setStreamErrorMessage(message);
    },
    onConversationIdGenerated: (id) => {
      streamAssignedIdRef.current = true;
      setIsLiveConversation(true);
      conversationState.setIsActivelyStreaming(true);
      conversationState.setConversationId(id);
      conversationState.setSharedConversationId(null);
      conversationState.setIsSharedView(false);
      conversationState.setSharedAtMessageIndex(void 0);
      setTimeout(() => refreshConversationsBase(), 1e3);
    }
  });
  const effectiveMessages = chat.messages.length > 0 ? chat.messages : conversationState.conversationHistory;
  const pageContext = usePageContext();
  const pendingAccessPointRef = useRef(null);
  const trackedConversationIdRef = useRef(null);
  const isLoadingExistingConversationRef = useRef(false);
  const isNewConversationRef = useRef(true);
  useEffect(() => {
    isNewConversationRef.current = effectiveMessages.length === 0;
  }, [effectiveMessages.length]);
  const { trackConversationStart } = useConciergeTracking({
    conversationId: conversationState.conversationId,
    pageContext
  });
  useEffect(() => {
    if (!conversationState.conversationId && !conversationState.sharedConversationId && !isLoadingExistingConversationRef.current) {
      pendingAccessPointRef.current = { accessPoint: "concierge_home", query: "" };
      trackedConversationIdRef.current = null;
    }
  }, [conversationState.conversationId, conversationState.sharedConversationId]);
  const previousConversationIdRef = useRef(null);
  useEffect(() => {
    const currentId = conversationState.conversationId;
    const previousId = previousConversationIdRef.current;
    if (currentId && currentId !== previousId && pendingAccessPointRef.current) {
      const { accessPoint, query } = pendingAccessPointRef.current;
      const isNew = previousId === null;
      trackConversationStart(accessPoint, query, isNew);
      trackedConversationIdRef.current = currentId;
      pendingAccessPointRef.current = null;
    }
    previousConversationIdRef.current = currentId;
  }, [conversationState.conversationId, trackConversationStart]);
  const GENERIC_INTERACTION_TYPES = useMemo(
    () => /* @__PURE__ */ new Set(["search_bar", "quick_prompts", "follow_up_question"]),
    []
  );
  const handleTrackAccessPoint = useCallback(
    (accessPoint, query) => {
      if (conversationState.conversationId && trackedConversationIdRef.current === conversationState.conversationId) {
        return;
      }
      if (pendingAccessPointRef.current && GENERIC_INTERACTION_TYPES.has(accessPoint)) {
        pendingAccessPointRef.current.query = query;
      } else {
        pendingAccessPointRef.current = { accessPoint, query };
      }
      if (conversationState.conversationId) {
        const { accessPoint: ap, query: q } = pendingAccessPointRef.current;
        const isNew = isNewConversationRef.current;
        trackConversationStart(ap, q, isNew);
        trackedConversationIdRef.current = conversationState.conversationId;
        pendingAccessPointRef.current = null;
      }
    },
    [conversationState.conversationId, trackConversationStart, GENERIC_INTERACTION_TYPES]
  );
  const {
    shouldScrollOnNextMessageRef
  } = useAutoScroll(
    effectiveMessages.length,
    conversationState.isActivelyStreaming,
    conversationState.streamingAIResponse,
    effectiveMessages.length === 0
  );
  useEffect(() => {
    const active = chat.isStreaming || chat.isLoading;
    conversationState.setIsActivelyStreaming(active);
    if (chat.isStreaming) {
      const lastMsg = chat.messages[chat.messages.length - 1];
      const hasActiveParts = (lastMsg == null ? void 0 : lastMsg.role) === "assistant" && Array.isArray(lastMsg.parts) && lastMsg.parts.some((p) => {
        var _a2;
        return p.type !== "text" || ((_a2 = p.text) == null ? void 0 : _a2.length) > 0;
      });
      if (!hasActiveParts) {
        const text = (lastMsg == null ? void 0 : lastMsg.role) === "assistant" ? lastMsg.parts.filter((p) => p.type === "text" && "text" in p).map((p) => p.text).join("") : "";
        conversationState.setStreamingAIResponse(text);
      }
    }
    if (!active) {
      conversationState.clearStreamingState();
    }
  }, [chat.isStreaming, chat.isLoading, chat.messages]);
  const performSearch = useCallback(
    async (text, files) => {
      shouldScrollOnNextMessageRef.current = true;
      await chat.sendMessage((files == null ? void 0 : files.length) ? { text, files } : text);
    },
    [chat, shouldScrollOnNextMessageRef]
  );
  const agreementGate = useConciergeAgreementGate({
    appConfigId,
    apiBaseUrl: conversationState.apiBaseUrl,
    messages: effectiveMessages,
    onShown: useCallback(() => {
      track(AnalyticsTrackingEvent.CONCIERGE_AGREEMENT_SHOWN, {
        appConfigId: appConfigId || void 0
      });
    }, [appConfigId]),
    onAccepted: useCallback(() => {
      track(AnalyticsTrackingEvent.CONCIERGE_AGREEMENT_ACCEPTED, {
        appConfigId: appConfigId || void 0
      });
    }, [appConfigId])
  });
  const handleAgreementAccept = useCallback(() => {
    const pending = agreementGate.accept();
    if (pending) {
      void performSearch(pending.text, pending.files);
    }
  }, [agreementGate, performSearch]);
  const cancelStream = useCallback(() => {
    chat.stop();
  }, [chat]);
  const resetChatMessages = useCallback(() => {
    var _a2;
    (_a2 = chat.setMessages) == null ? void 0 : _a2.call(chat, []);
  }, [chat]);
  const handleSidebarToggle = useCallback(() => {
    setIsSidebarExpanded((prev) => !prev);
  }, []);
  const handleChatsClick = useCallback(() => {
    if (onChatsClick) {
      onChatsClick();
    } else if (!isSidebarExpanded) {
      setIsSidebarExpanded(true);
    }
  }, [isSidebarExpanded, onChatsClick]);
  const queryClient = useQueryClient();
  const refreshConversationsBase = useConversationRefresh();
  const refreshConversationHistory = useCallback(() => {
    refreshConversationsBase();
    setTimeout(() => refreshConversationsBase(), 5e3);
  }, [refreshConversationsBase]);
  const handleStopWithRollback = useCallback(() => {
    void (async () => {
      const restoredPrompt = await chat.cancelLatestTurn();
      conversationState.setIsActivelyStreaming(false);
      conversationState.clearStreamingState();
      if (restoredPrompt) {
        setSearchInputValue(restoredPrompt);
      }
      refreshConversationHistory();
    })();
  }, [chat, conversationState, refreshConversationHistory]);
  const handleSearch = async (searchTerm, files) => {
    setSearchInputValue("");
    setStreamErrorMessage(null);
    if (agreementGate.interceptSend(searchTerm, files)) {
      return;
    }
    refreshConversationHistory();
    await performSearch(searchTerm, files);
  };
  const handleStreamErrorRetry = useCallback(() => {
    const text = searchInputValue.trim();
    setStreamErrorMessage(null);
    if (!text) return;
    void handleSearch(text);
  }, [searchInputValue, handleSearch]);
  const handleSearchRef = useRef(handleSearch);
  useEffect(() => {
    handleSearchRef.current = handleSearch;
  }, [handleSearch]);
  const prevIsSignedInRef = useRef(void 0);
  useEffect(() => {
    const wasSignedIn = prevIsSignedInRef.current;
    prevIsSignedInRef.current = conversationState.isSignedIn;
    if (wasSignedIn !== false || !conversationState.isSignedIn) return;
    setTimeout(() => void performSearch("I just signed in."), 300);
  }, [conversationState.isSignedIn, performSearch]);
  const handleUpdateFeedback = (messageIndex, feedback) => {
    conversationState.setConversationHistory((prev) => {
      return prev.map((msg, idx) => {
        if (idx === messageIndex) {
          return {
            ...msg,
            feedback: feedback || void 0
          };
        }
        return msg;
      });
    });
  };
  const [shareDialog, setShareDialog] = useState({ open: false, conversationId: null });
  const handleShareChat = useCallback((conversationId) => {
    setShareDialog({ open: true, conversationId, messageIndex: void 0 });
  }, []);
  useEffect(() => {
    if (conversationState.pendingShareConversationId) {
      setShareDialog({
        open: true,
        conversationId: conversationState.pendingShareConversationId,
        messageIndex: void 0
      });
      conversationState.setPendingShareConversationId(null);
    }
  }, [conversationState.pendingShareConversationId]);
  const handleOpenShareDialog = useCallback((conversationId, messageIndex) => {
    setShareDialog({ open: true, conversationId, messageIndex });
  }, []);
  const handleShareDialogClose = useCallback(() => {
    setShareDialog((s) => ({ ...s, open: false, conversationId: null }));
  }, []);
  const handleShareCopyLink = useCallback(async () => {
    const { conversationId: id, messageIndex } = shareDialog;
    if (!id) throw new Error("Missing conversationId");
    const headers = await getHeadersWithUniqueVisitorId();
    const result = await setConversationSharing({
      conversationId: id,
      enabled: true,
      messageIndex,
      headersToInject: headers,
      apiBaseUrl: conversationState.apiBaseUrl
    });
    return result.shareUrl;
  }, [shareDialog.conversationId, shareDialog.messageIndex, conversationState.apiBaseUrl]);
  const handleConversationSelect = (conversationId) => {
    pendingAccessPointRef.current = null;
    cancelStream();
    resetChatMessages();
    setIsLiveConversation(false);
    conversationState.setIsActivelyStreaming(false);
    conversationState.clearStreamingState();
    if (conversationState.conversationId === conversationId) {
      isLoadingExistingConversationRef.current = true;
      conversationState.setConversationId(null);
      setTimeout(() => {
        conversationState.setConversationId(conversationId);
        isLoadingExistingConversationRef.current = false;
      }, 0);
      return;
    }
    conversationState.setConversationHistory([]);
    conversationState.setStreamingAIResponse("");
    conversationState.setFollowUpQuestions([]);
    conversationState.setIsSharedView(false);
    conversationState.setSharedByUserName(void 0);
    conversationState.setSharedAtMessageIndex(void 0);
    conversationState.setSourceConversationId(void 0);
    conversationState.setSourceCheckpointIndex(void 0);
    conversationState.setSharedConversationId(null);
    const params2 = new URLSearchParams(window.location.search);
    params2.set("conversationId", conversationId);
    params2.delete("sharedConversationId");
    const newUrl = `${window.location.pathname}?${params2.toString()}`;
    window.history.pushState({}, "", newUrl);
    conversationState.setConversationId(conversationId);
    conversationState.setSharedConversationId(null);
  };
  const handleComposeClick = () => {
    cancelStream();
    resetChatMessages();
    agreementGate.reset();
    setIsLiveConversation(false);
    conversationState.setIsActivelyStreaming(false);
    conversationState.clearStreamingState();
    conversationState.setConversationHistory([]);
    conversationState.setStreamingAIResponse("");
    conversationState.setFollowUpQuestions([]);
    conversationState.setConversationId(null);
    conversationState.setIsSharedView(false);
    conversationState.setSharedByUserName(void 0);
    conversationState.setSharedAtMessageIndex(void 0);
    conversationState.setSourceConversationId(void 0);
    conversationState.setSourceCheckpointIndex(void 0);
    conversationState.setSharedConversationId(null);
    const params2 = new URLSearchParams(window.location.search);
    params2.delete("conversationId");
    params2.delete("sharedConversationId");
    const newUrl = params2.toString() ? `${window.location.pathname}?${params2.toString()}` : window.location.pathname;
    window.history.pushState({}, "", newUrl);
  };
  const prevConversationIdForCleanupRef = useRef(conversationState.conversationId);
  useEffect(() => {
    const prev = prevConversationIdForCleanupRef.current;
    const curr = conversationState.conversationId;
    prevConversationIdForCleanupRef.current = curr;
    if (prev === curr) return;
    const isStreamIdAssignment = prev === null && curr !== null && (chat.isStreaming || chat.isLoading || streamAssignedIdRef.current);
    streamAssignedIdRef.current = false;
    if (isStreamIdAssignment) return;
    if (chat.isStreaming || chat.isLoading) {
      chat.stop();
    }
    resetChatMessages();
    setIsLiveConversation(false);
    conversationState.setIsActivelyStreaming(false);
    conversationState.clearStreamingState();
    conversationState.setConversationHistory([]);
    conversationState.setFollowUpQuestions([]);
    conversationState.setIsSharedView(false);
    conversationState.setSharedByUserName(void 0);
    conversationState.setSharedAtMessageIndex(void 0);
    conversationState.setSourceConversationId(void 0);
    conversationState.setSourceCheckpointIndex(void 0);
    setStreamErrorMessage(null);
  }, [conversationState.conversationId]);
  const {
    isLoadingConversationQuery,
    isPendingAssistantResponse,
    isPollingTimedOut,
    resetPendingState,
    markConversationStale
  } = useConversationLoader({
    conversationId: conversationState.conversationId,
    sharedConversationId: conversationState.sharedConversationId,
    isActivelyStreaming: conversationState.isActivelyStreaming,
    conversationHistoryLength: effectiveMessages.length,
    apiBaseUrl: conversationState.apiBaseUrl,
    viewToken,
    onConversationLoaded: (messages, followUpQuestions, sharingInfo) => {
      var _a2;
      setIsLiveConversation(false);
      conversationState.setConversationHistory(messages);
      conversationState.setFollowUpQuestions(followUpQuestions);
      if (!isLiveConversation) {
        (_a2 = chat.setMessages) == null ? void 0 : _a2.call(chat, messages);
      }
      if (sharingInfo) {
        conversationState.setIsSharedView(sharingInfo.isSharedView ?? false);
        conversationState.setSharedConversationId(sharingInfo.sharedConversationId || null);
        conversationState.setSharedByUserName(sharingInfo.sharedByUserName);
        conversationState.setSharedAtMessageIndex(sharingInfo.sharedAtMessageIndex);
        conversationState.setSourceConversationId(sharingInfo.sourceConversationId);
        conversationState.setSourceCheckpointIndex(sharingInfo.sourceCheckpointIndex);
      } else {
        conversationState.setSharedConversationId(null);
        conversationState.setIsSharedView(false);
        conversationState.setSharedByUserName(void 0);
        conversationState.setSharedAtMessageIndex(void 0);
        conversationState.setSourceConversationId(void 0);
        conversationState.setSourceCheckpointIndex(void 0);
      }
    },
    onConversationNotFound: () => {
      const isInitialUrlCleanup = conversationState.conversationId === initialConversationIdRef.current && conversationState.sharedConversationId === initialSharedConversationIdRef.current;
      if (!isInitialUrlCleanup || viewToken) {
        toast.error(
          viewToken ? "Unable to load conversation preview. The link may have expired." : "Conversation not found."
        );
      }
      const params2 = new URLSearchParams(window.location.search);
      params2.delete("conversationId");
      params2.delete("sharedConversationId");
      const newUrl = params2.toString() ? `${window.location.pathname}?${params2.toString()}` : window.location.pathname;
      window.history.replaceState({}, "", newUrl);
      conversationState.setConversationId(null);
      conversationState.setSharedConversationId(null);
    }
  });
  const handleRetry = () => {
    resetPendingState();
    const history = conversationState.conversationHistory;
    if (history.length === 0) return;
    const lastMsg = history[history.length - 1];
    if (lastMsg.role !== "user") return;
    const lastUserText = (lastMsg.parts || []).filter((p) => p.type === "text" && typeof p.text === "string").map((p) => p.text).join("").trim();
    if (!lastUserText) return;
    conversationState.setConversationHistory(history.slice(0, -1));
    handleSearch(lastUserText);
  };
  const wasStreamingRef = useRef(false);
  useEffect(() => {
    if (wasStreamingRef.current && !conversationState.isActivelyStreaming) {
      refreshConversationHistory();
    }
    wasStreamingRef.current = conversationState.isActivelyStreaming;
  }, [conversationState.isActivelyStreaming, refreshConversationHistory]);
  const wasPendingRef = useRef(false);
  useEffect(() => {
    if (wasPendingRef.current && !isPendingAssistantResponse) {
      refreshConversationHistory();
    }
    wasPendingRef.current = isPendingAssistantResponse;
  }, [isPendingAssistantResponse, refreshConversationHistory]);
  useUrlSync(conversationState.conversationId, conversationState.sharedConversationId);
  const wrappedOnSignOutClick = useCallback(() => {
    conversationState.setConversationId(null);
    conversationState.setSharedConversationId(null);
    conversationState.setConversationHistory([]);
    conversationState.clearStreamingState();
    resetChatMessages();
    queryClient.removeQueries({ queryKey: ["conversations"] });
    onSignOutClick == null ? void 0 : onSignOutClick();
  }, [conversationState, resetChatMessages, queryClient, onSignOutClick]);
  useAutoSendFromUrl({
    isActivelyStreaming: conversationState.isActivelyStreaming,
    conversationLength: effectiveMessages.length,
    onSearch: handleSearch
  });
  useEffect(() => {
    if (conversationState.conversationId === null && conversationState.sharedConversationId === null && effectiveMessages.length > 0 && !conversationState.isActivelyStreaming && !chat.isStreaming && !chat.isLoading && !isLoadingConversationQuery) {
      resetChatMessages();
      conversationState.setConversationHistory([]);
    }
  }, [
    // eslint-disable-line react-hooks/exhaustive-deps
    conversationState.conversationId,
    conversationState.sharedConversationId,
    effectiveMessages.length,
    conversationState.isActivelyStreaming,
    chat.isStreaming,
    chat.isLoading,
    isLoadingConversationQuery
  ]);
  const showWelcome = effectiveMessages.length === 0 && !isLoadingConversationQuery && !isPendingAssistantResponse && !conversationState.conversationId && !conversationState.sharedConversationId;
  const chatAreaProps = {
    appName,
    conciergeName: studioAgentName || (brandAppConfig == null ? void 0 : brandAppConfig.conciergeAgentName),
    brandId: (brandId == null ? void 0 : brandId.toString()) ?? null,
    conversationId: conversationState.conversationId,
    apiBaseUrl: conversationState.apiBaseUrl,
    isEmbedded,
    userFirstName,
    conversationHistory: effectiveMessages,
    followUpQuestions: conversationState.followUpQuestions,
    latestFollowUps: chat.latestFollowUps,
    isLoadingFollowUps: chat.isLoadingFollowUps,
    streamingDevInfo: conversationState.streamingDevInfo,
    isActivelyStreaming: conversationState.isActivelyStreaming,
    transientStatus: chat.transientStatus,
    streamingAIResponse: conversationState.streamingAIResponse,
    isLoadingConversationQuery,
    isPendingAssistantResponse,
    isPollingTimedOut,
    onRetry: handleRetry,
    onRetryLastMessage: chat.retryLastMessage,
    streamErrorMessage,
    onStreamErrorRetry: handleStreamErrorRetry,
    onLiveTakeoverActive: resetPendingState,
    onTakeoverConversationChange: setLiveTakeoverActiveConversationId,
    onSupportEventsReconnect: markConversationStale,
    handleSearch,
    onUpdateFeedback: handleUpdateFeedback,
    onVariantSelect,
    onTrackAccessPoint: handleTrackAccessPoint,
    onStop: handleStopWithRollback,
    searchInputValue,
    setSearchInputValue,
    brandLogoUrl: appLogoUrl,
    onHomeClick,
    ...layout === "sidebar" && {
      isSidebarExpanded
    },
    // Mobile sidebar overlay props - enables the hamburger menu on mobile
    ...viewMode !== "read-only" && {
      mobileSidebarProps: {
        onConversationSelect: handleConversationSelect,
        onComposeClick: handleComposeClick,
        onChatsClick: handleChatsClick,
        currentConversationId: conversationState.conversationId,
        userProfile,
        apiBaseUrl: conversationState.apiBaseUrl,
        onSignOutClick: wrappedOnSignOutClick,
        onProfileClick,
        onPurchasesClick,
        onHomeClick,
        brandLogoUrl: appLogoUrl,
        onShareChat: handleShareChat,
        liveTakeoverActiveConversationId
      }
    },
    // Sharing state
    isSharedView: conversationState.isSharedView,
    sharedByUserName: conversationState.sharedByUserName,
    sourceConversationId: conversationState.sourceConversationId,
    sourceCheckpointIndex: conversationState.sourceCheckpointIndex,
    // Open the shared "Share this conversation" dialog (from a message or sidebar)
    ...viewMode !== "read-only" && { onOpenShareDialog: handleOpenShareDialog },
    onShareClick: viewMode !== "read-only" && conversationState.conversationId && !showWelcome && !conversationState.isSharedView ? () => handleShareChat(conversationState.conversationId) : void 0,
    viewMode
  };
  const devOptionsPanel = /* @__PURE__ */ jsx(DevOptionsPanel, { ...devOpts });
  const checkoutDrawer = CheckoutDrawerContent && /* @__PURE__ */ jsx(
    Drawer,
    {
      id: "cart-popover",
      open: isCheckoutDrawerOpen,
      sx: {
        zIndex: 1300,
        width: 462,
        maxWidth: "100%"
      },
      PaperProps: {
        sx: {
          boxShadow: "none",
          borderRadius: "0",
          maxWidth: "100%"
        }
      },
      anchor: "right",
      disableAutoFocus: true,
      disableEnforceFocus: true,
      disableRestoreFocus: true,
      children: /* @__PURE__ */ jsx(CheckoutDrawerContent, {})
    }
  );
  const shareDialogEl = shareDialog.open && shareDialog.conversationId && /* @__PURE__ */ jsx(
    ShareConversationDialog,
    {
      open: shareDialog.open,
      onClose: handleShareDialogClose,
      onCopyLink: handleShareCopyLink,
      brandLogoUrl: appLogoUrl
    }
  );
  const brandFontStyle = brandFontFamily ? /* @__PURE__ */ jsx(
    "style",
    {
      dangerouslySetInnerHTML: {
        __html: `[data-radix-popper-content-wrapper]{font-family:${brandFontFamily}!important}`
      }
    }
  ) : null;
  const agreementOverlay = agreementGate.shouldShowCard && agreementGate.agreement ? /* @__PURE__ */ jsx(
    ConciergeAgreementCard,
    {
      agreement: agreementGate.agreement,
      themeColors: {
        textPrimary: themeColors.textPrimary,
        primary: themeColors.primary,
        buttonText: themeColors.buttonText
      },
      pendingUserPrompt: (_a = agreementGate.pending) == null ? void 0 : _a.text,
      onAgree: handleAgreementAccept
    }
  ) : null;
  const showShareButton = layout === "content-only" && viewMode !== "read-only" && !!conversationState.conversationId && !showWelcome && !conversationState.isSharedView;
  if (layout === "content-only") {
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(Toaster, {}),
      brandFontStyle,
      shareDialogEl,
      /* @__PURE__ */ jsxs(
        "div",
        {
          className: "concierge-root tw:relative tw:flex tw:flex-1 tw:flex-col",
          style: { minWidth: 0, height: "100%", ...brandFontFamily ? { fontFamily: brandFontFamily } : {} },
          children: [
            showShareButton && /* @__PURE__ */ jsxs(Fragment, { children: [
              /* @__PURE__ */ jsx(
                "style",
                {
                  dangerouslySetInnerHTML: {
                    __html: "@media(max-width:1279px){.concierge-desktop-share{display:none!important}}"
                  }
                }
              ),
              /* @__PURE__ */ jsxs(
                "div",
                {
                  role: "button",
                  tabIndex: 0,
                  className: "concierge-desktop-share",
                  onClick: () => handleShareChat(conversationState.conversationId),
                  onKeyDown: (e) => {
                    if (e.key === "Enter" || e.key === " ") handleShareChat(conversationState.conversationId);
                  },
                  "aria-label": "Share conversation",
                  style: {
                    position: "absolute",
                    top: 8,
                    right: 16,
                    zIndex: 50,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "6px 12px",
                    borderRadius: 8,
                    cursor: "pointer",
                    fontSize: 14,
                    fontWeight: 500,
                    fontFamily: brandFontFamily || "inherit",
                    transition: "opacity 200ms ease"
                  },
                  onMouseEnter: (e) => {
                    e.currentTarget.style.opacity = "0.7";
                  },
                  onMouseLeave: (e) => {
                    e.currentTarget.style.opacity = "1";
                  },
                  children: [
                    /* @__PURE__ */ jsx("svg", { xmlns: "http://www.w3.org/2000/svg", width: "14", height: "14", viewBox: "0 0 15 15", fill: "none", children: /* @__PURE__ */ jsx(
                      "path",
                      {
                        d: "M7.1478e-07 9.99805V9.83203C7.1478e-07 9.46476 0.29777 9.16699 0.66504 9.16699C1.03231 9.16699 1.33008 9.46476 1.33008 9.83203V9.99805C1.33008 10.7089 1.33099 11.2025 1.36231 11.5859C1.39299 11.9615 1.44982 12.1731 1.53027 12.3311L1.60059 12.458C1.7769 12.7454 2.02992 12.9799 2.33203 13.1338L2.46191 13.1895C2.60569 13.2409 2.7952 13.2787 3.07715 13.3018C3.46067 13.3331 3.954 13.333 4.66504 13.333H9.99805C10.7089 13.333 11.2025 13.3331 11.5859 13.3018C11.9613 13.2711 12.1731 13.2142 12.3311 13.1338L12.458 13.0625C12.7454 12.8862 12.9799 12.6331 13.1338 12.3311L13.1895 12.2012C13.2409 12.0574 13.2787 11.8677 13.3018 11.5859C13.3331 11.2025 13.333 10.7089 13.333 9.99805V9.83203C13.333 9.46487 13.6309 9.16717 13.998 9.16699C14.3653 9.16699 14.6631 9.46476 14.6631 9.83203V9.99805C14.6631 10.6871 14.6637 11.2441 14.627 11.6943C14.5942 12.0953 14.5292 12.4565 14.3848 12.793L14.3184 12.9355C14.0529 13.4565 13.649 13.8922 13.1533 14.1963L12.9355 14.3184C12.5588 14.5103 12.1523 14.5895 11.6943 14.627C11.2441 14.6637 10.6871 14.6631 9.99805 14.6631H4.66504C3.97595 14.6631 3.41899 14.6637 2.96875 14.627C2.56821 14.5942 2.20731 14.5299 1.87109 14.3857L1.72852 14.3184C1.2074 14.0528 0.770898 13.6491 0.466798 13.1533L0.344727 12.9355C0.152758 12.5588 0.073554 12.1523 0.0361335 11.6943C-0.000636417 11.2441 7.1478e-07 10.687 7.1478e-07 9.99805ZM6.66699 9.83203V2.27051L4.46875 4.46875C4.20911 4.72839 3.78803 4.72829 3.52832 4.46875C3.26862 4.20905 3.26862 3.78802 3.52832 3.52832L6.86133 0.194336L6.96289 0.111328C7.07128 0.0391338 7.19987 9.45271e-09 7.33203 0C7.50813 8.62398e-05 7.67715 0.0699043 7.80176 0.194336L11.1357 3.52832C11.3951 3.78799 11.3952 4.20915 11.1357 4.46875C10.876 4.72845 10.454 4.72845 10.1943 4.46875L7.99707 2.27148V9.83203C7.9969 10.199 7.69904 10.4969 7.33203 10.4971C6.96487 10.4971 6.66717 10.1992 6.66699 9.83203Z",
                        fill: "currentColor"
                      }
                    ) }),
                    /* @__PURE__ */ jsx("span", { children: "Share" })
                  ]
                }
              )
            ] }),
            devOptionsPanel,
            /* @__PURE__ */ jsx(ConciergeChatAreaV2, { ...chatAreaProps }),
            agreementOverlay
          ]
        }
      ),
      checkoutDrawer
    ] });
  }
  if (layout === "sidebar") {
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(Toaster, {}),
      brandFontStyle,
      shareDialogEl,
      /* @__PURE__ */ jsx(SupportThemeProvider, { children: /* @__PURE__ */ jsxs(
        "div",
        {
          className: "concierge-root",
          style: {
            display: "flex",
            flexDirection: "row",
            height: "100dvh",
            minHeight: "-webkit-fill-available",
            ...brandFontFamily ? { fontFamily: brandFontFamily } : {}
          },
          children: [
            viewMode !== "read-only" && /* @__PURE__ */ jsx(
              ConciergeSidebar,
              {
                onConversationSelect: handleConversationSelect,
                onComposeClick: handleComposeClick,
                onChatsClick: handleChatsClick,
                currentConversationId: conversationState.conversationId,
                userProfile,
                apiBaseUrl: conversationState.apiBaseUrl,
                onSignOutClick: wrappedOnSignOutClick,
                onProfileClick,
                onPurchasesClick,
                onHomeClick,
                brandLogoUrl: appLogoUrl,
                isExpanded: isSidebarExpanded,
                onToggle: handleSidebarToggle,
                onShareChat: handleShareChat,
                liveTakeoverActiveConversationId
              }
            ),
            /* @__PURE__ */ jsxs("div", { className: "tw:relative tw:flex tw:flex-1 tw:flex-col", style: { minWidth: 0 }, children: [
              devOptionsPanel,
              /* @__PURE__ */ jsx(ConciergeChatAreaV2, { ...chatAreaProps }),
              agreementOverlay
            ] })
          ]
        }
      ) }),
      checkoutDrawer
    ] });
  }
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Toaster, {}),
    brandFontStyle,
    shareDialogEl,
    /* @__PURE__ */ jsxs(
      "div",
      {
        className: "concierge-root",
        style: {
          display: "flex",
          flexDirection: "column",
          height: "100dvh",
          minHeight: "-webkit-fill-available",
          ...brandFontFamily ? { fontFamily: brandFontFamily } : {}
        },
        children: [
          /* @__PURE__ */ jsx(
            ConciergeHeader,
            {
              onConversationSelect: handleConversationSelect,
              onComposeClick: handleComposeClick,
              showComposeIcon: !showWelcome,
              cartCount,
              onBagClick,
              onShareClick: viewMode !== "read-only" && conversationState.conversationId && !showWelcome && !conversationState.isSharedView ? () => handleShareChat(conversationState.conversationId) : void 0,
              viewMode
            }
          ),
          devOptionsPanel,
          /* @__PURE__ */ jsxs("div", { className: "tw:relative tw:flex tw:flex-1 tw:flex-col", style: { minWidth: 0 }, children: [
            /* @__PURE__ */ jsx(ConciergeChatAreaV2, { ...chatAreaProps }),
            agreementOverlay
          ] })
        ]
      }
    ),
    checkoutDrawer
  ] });
};
export {
  ConciergeContainer as default
};
//# sourceMappingURL=ConciergeContainer.js.map
